import { useState, useEffect } from 'react';
import { Button, EmptyState, LoadingState } from '@/components/ui';
import { CollectionCard, CollectionDetailModal } from '../components';
import { getCollections, type CollectionSummary } from '@/api/onboarding';
import type { WizardData, CollectionOption } from '../types';

// Default image for collections without imageUrl
const DEFAULT_COLLECTION_IMAGE =
  'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400&h=300&fit=crop';

/**
 * Map API CollectionSummary to UI CollectionOption
 */
function mapToCollectionOption(summary: CollectionSummary): CollectionOption {
  return {
    id: summary.id,
    title: summary.name,
    description: summary.description || '',
    image: summary.imageUrl || DEFAULT_COLLECTION_IMAGE,
    products: summary.products,
    colors: summary.colors,
    style: summary.style || '',
  };
}

interface StepCollectionProps {
  data: WizardData;
  onChange: (updates: Partial<WizardData>) => void;
}

/**
 * Step 3: Collection Selection
 * Loads collections from API and allows user to choose one
 */
export function StepCollection({ data, onChange }: StepCollectionProps) {
  const [collections, setCollections] = useState<CollectionOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailCollection, setDetailCollection] =
    useState<CollectionOption | null>(null);

  // Load collections from API
  useEffect(() => {
    async function loadCollections() {
      try {
        setLoading(true);
        setError(null);
        const data = await getCollections();
        setCollections(data.map(mapToCollectionOption));
      } catch (err) {
        console.error('Failed to load collections:', err);
        setError('Failed to load collections. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    loadCollections();
  }, []);

  if (loading) {
    return <LoadingState label="Loading collections..." />;
  }

  if (error) {
    return (
      <EmptyState
        title="Collections unavailable"
        description={error}
        action={
          <Button variant="primary" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        }
      />
    );
  }

  if (collections.length === 0) {
    return <EmptyState title="No collections available." />;
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-ink-2">
        Choose a collection for your shop. Each collection has a matching theme,
        color palette and product selection.
      </p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {collections.map((c) => (
          <CollectionCard
            key={c.id}
            collection={c}
            selected={data.collectionId === c.id}
            onSelect={() =>
              onChange({ collectionId: c.id, collectionName: c.title })
            }
            onShowDetails={() => setDetailCollection(c)}
          />
        ))}
      </div>

      <CollectionDetailModal
        collection={detailCollection}
        isOpen={!!detailCollection}
        onClose={() => setDetailCollection(null)}
        onSelect={() => {
          if (detailCollection) {
            onChange({
              collectionId: detailCollection.id,
              collectionName: detailCollection.title,
            });
            setDetailCollection(null);
          }
        }}
        isSelected={detailCollection?.id === data.collectionId}
      />
    </div>
  );
}
